<template>
    <v-layout row>
        <v-flex xs12 sm12>
            <v-card class="elevation-1 transparent">
                <v-toolbar class="transparent" flat height="37">
                    <!-- Back Button -->
                    <v-btn dark icon title="Back to previous page" @click="$router.go(-1)">
                        <v-icon>arrow_back</v-icon>
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-chip v-if="!isMyLedger" class="user-chip-container">
                        <v-avatar>
                            <v-img ref="avatar" :src="awsS3Helper.getAvatarUrl(user_data.userId)"></v-img>
                        </v-avatar>
                        <b>{{user_data.firstName}} {{user_data.lastName}}</b>
                    </v-chip>
                </v-toolbar>
            </v-card>
            
            <v-alert v-if="isProRated" :value="true" color="info" icon="info" outline class="mt-8 rounded-4">
                <strong>For Prorated Leaves:</strong>
                <ul class="mb-0 mt-1">
                    <li><strong>Monthly Variations:</strong> Credits are calculated daily based on active employment days. Varied month lengths mean monthly accruals are not identical.</li>
                    <li><strong>Transaction Dates:</strong> The 1st of the month is a system baseline placeholder, not a lump-sum entry date.</li>
                    <li><strong>Current Month:</strong> The ongoing month shows a fractional credit tracked precisely up to today's date.</li>
                </ul>
            </v-alert>

            <!-- Ledger Grid Section -->
            <v-card class="elevation-1 mt-5"> 
                <v-toolbar flat height="55px">
                    <div>
                        <b>{{ selectedLeaveName }}</b>
                        <small class="grey--text text--darken-3 d-block mt-0">*Values are in Workdays (1 Workday = 8 Hours)</small>
                    </div>
                </v-toolbar>
                
                <v-data-table
                    :headers="isMobile ? [] : headers"
                    :items="ledger_records"
                    :loading="loading"
                    :rows-per-page-items="rowsPerPageItems"
                    :pagination.sync="paginationSync"
                    :hide-headers="isMobile"
                >
                    <template v-slot:items="props">
                        <!-- Adaptive Mobile View -->
                        <tr v-if="isMobile">
                            <td :colspan="headers.length" class="pa-1">
                                <v-card class="elevation-1" @click="viewDetails(props.item)">
                                    <v-toolbar flat height="42">
                                        <span>{{ props.item.transactionDate | moment('MMM D, YYYY') }}</span>
                                        <v-spacer></v-spacer>
                                        <!-- Mobile Balance Dual Format -->
                                        <div class="text-xs-right">
                                            <span class="font-weight-bold primary--text d-block">Bal: {{ numFormat.formatNumber(props.item.runningBalance, 4) }}</span>
                                            <small class="primary--text d-block font-weight-bold">{{ decimalWorkDaysToHoursMinutes(props.item.runningBalance) }}</small>
                                        </div>
                                    </v-toolbar>
                                    <v-layout row wrap align-center class="section-layout py-2">
                                        <v-flex xs12>
                                            <div class="px-3 pb-2">{{ props.item.remarks }}</div>
                                            <v-layout row class="px-3">
                                                <v-flex xs6>
                                                    <div class="caption grey--text text--darken-3">Credit (+)</div>
                                                    <div :class="props.item.credit > 0 ? 'success--text font-weight-bold' : ''">
                                                        <span>{{ props.item.credit > 0 ? numFormat.formatNumber(props.item.credit, 4) : '-' }}</span>
                                                        <small v-if="props.item.credit > 0" class="grey--text d-block font-weight-regular">{{ decimalWorkDaysToHoursMinutes(props.item.credit) }}</small>
                                                    </div>
                                                </v-flex>
                                                <v-flex xs6>
                                                    <div class="caption grey--text text--darken-3">Debit (-)</div>
                                                    <div :class="props.item.debit > 0 ? 'error--text font-weight-bold' : ''">
                                                        <span>{{ props.item.debit > 0 ? numFormat.formatNumber(props.item.debit, 4) : '-' }}</span>
                                                        <small v-if="props.item.debit > 0" class="grey--text text--darken-3 d-block font-weight-regular">{{ decimalWorkDaysToHoursMinutes(props.item.debit) }}</small>
                                                    </div>
                                                </v-flex>
                                            </v-layout>
                                        </v-flex>
                                    </v-layout>
                                </v-card>
                            </td>
                        </tr>

                        <!-- Standard Desktop Ledger View -->
                        <tr v-else @click="viewDetails(props.item)" class="clickable" title="Click to view details">
                            <td>{{ props.item.transactionDate | moment('MMM D, YYYY') }}</td>
                            <td>{{ props.item.remarks }}</td>
                            
                            <!-- Desktop Credit Column -->
                            <td class="success--text font-weight-medium">
                                <div v-if="props.item.credit > 0">
                                    <div>{{ numFormat.formatNumber(props.item.credit, 4) }}</div>
                                    <div class="caption grey--text text--darken-3">{{ decimalWorkDaysToHoursMinutes(props.item.credit) }}</div>
                                </div>
                                <span v-else>-</span>
                            </td>
                            
                            <!-- Desktop Debit Column -->
                            <td class="error--text font-weight-medium">
                                <div v-if="props.item.debit > 0">
                                    <div>{{ numFormat.formatNumber(props.item.debit, 4) }}</div>
                                    <div class="caption grey--text text--darken-3">{{ decimalWorkDaysToHoursMinutes(props.item.debit) }}</div>
                                </div>
                                <span v-else>-</span>
                            </td>
                            
                            <!-- Desktop Running Balance Column -->
                            <td class="font-weight-bold">
                                <div>{{ numFormat.formatNumber(props.item.runningBalance, 4) }}</div>
                                <div class="caption primary--text">{{ decimalWorkDaysToHoursMinutes(props.item.runningBalance) }}</div>
                            </td>
                        </tr>
                    </template>
                </v-data-table> 
            </v-card>

        </v-flex>
    </v-layout> 
</template>

<script>

import timeoffApi from '@/api/timeoffs'
import leaveTypesApi from '@/api/leavetypes'
import commonfn from '@/common/functions'   //for refactor
import numFormat from '@/common/num_format'
import awsS3Helper from '@/helpers/aws-s3'
import pageKeys from '@/common/pagekeys'
import rowsPerPageItems from '@/constants/rows-per-page'
import { getRowsPerPage, updateRowsPerPage } from '@/helpers/user-prefs'
import { decimalWorkDaysToHoursMinutes } from '@/helpers/time-converter'

const ROUTE_MAP = {
    TIME_OFF: '/myattendance/timeoff/details/',
    OVERTIME: '/myattendance/overtime/details/',
    LEAVE_BENEFITS: '/compensation/'
};

export default {     
    name: 'LeaveBalancesByEmployee',
    metaInfo: {
        title: 'Employee Leave Ledger',
        meta: [
            { charset: 'utf-8' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' }
        ]
    },
    data () {
        const routeName = this.$route.name

        return {
            commonfn,
            awsS3Helper,
            numFormat,
            rowsPerPageItems,
            employeeName: '',
            menu_status: false,
            menu_balances: false,
            paginationSync: {
                rowsPerPage: getRowsPerPage(routeName),
                sortBy: null, // Change 'transactionDate' to null
                descending: false,
            },
            headers: [
                { text: 'Transaction Date', align: 'left', value: 'transactionDate', width: '15%', sortable: false },
                { text: 'Particulars / Remarks', align: 'left', value: 'remarks', width: '37%', sortable: false },
                { text: 'Credit', align: 'left', value: 'credit', width: '16%', sortable: false },
                { text: 'Debit', align: 'left', value: 'debit', width: '16%', sortable: false },
                { text: 'Balance', align: 'left', value: 'runningBalance', width: '16%', sortable: false },
            ],
            leave_types: [],
            user_data: null,
            ledger_records: [], // Binds the raw flat computed data returned straight from DB SP
            error: false,
            errorMessage: '',
            loading: false
        }
    },
    methods: {
        decimalWorkDaysToHoursMinutes,
        viewDetails({ requestType, requestId }) {
            const baseRoute = ROUTE_MAP[requestType];
            if (!baseRoute) return;

            if (requestType === 'LEAVE_BENEFITS') {
                const path = this.isMyLedger ? 'mycompensation' : `allocation/${this.user_data.userId}/leave-benefits`;
                this.$router.push(`${baseRoute}${path}`);
            } else {
                this.$router.push(`${baseRoute}${requestId}`);
            }
        },
        async getLeaveTypes() {
            try {
                this.loading = true
                const response = await leaveTypesApi.getByCompany()
                if (response && response.status == 200) {
                    this.leave_types = response.data.items
                    // Safely defaults first choice to Vacation Leave ('VL') per specifications
                    const defaultLeave = this.leave_types.find(x => x.leaveCode == 'VL')
                    this.leaveTypeId = defaultLeave ? defaultLeave.leaveTypeId : this.leave_types[0].leaveTypeId
                }
                this.loading = false
            } catch (error) {
                this.error = true
                this.errorMessage = error
                this.loading = false
            }
        },
        async getEmployeeLedgerHistory() {
            if (!this.leaveTypeId) return
            try {
                this.loading = true
                
                // Assuming an API target mapping to the new Employee Ledger SP data payload
                // Passing context parameters: Employee ID, Target Leave Type ID, and Year
                const response = await timeoffApi.getTransactionLedger(this.userId, this.leaveTypeId, this.fiscalYear, this.pageKey)
                
                if (response && response.status == 200) {
                    this.user_data = response.data.user || null
                    this.ledger_records = response.data.items || []
                }
                this.loading = false
            } catch (error) {
                this.error = true
                this.errorMessage = error
                this.loading = false
            }
        },
        async initialize() {
            await this.getLeaveTypes()
            await this.getEmployeeLedgerHistory()
        }
    },
    async created() {
        this.initialize()

        this.$root.$emit('setHeaderTitles', {
            display: true,
            headerTitle: '',
            headerPageTitle: 'Leave Transaction Ledger'
        })
    },
    watch: {
        'paginationSync.rowsPerPage'(newVal) {
            updateRowsPerPage(this.$route.name, newVal)
        }
    },
    computed: {
        isMyLedger() {
            return !this.$route.params.userId
        },
        userId() {
            return this.isMyLedger ? this.$store.state.userId : this.$route.params.userId
        },
        leaveTypeId() {
            return this.$route.params.leaveTypeId
        },
        fiscalYear() {
            return this.$route.params.fiscalYear || new Date().getFullYear()
        },
        isProRated() {
            return this.$route.params.proRated === 'true'
        },
        pageKey() {
            return this.isMyLedger ? pageKeys.timeoff : pageKeys.timeoff_team_approver
        },
        selectedLeaveName() {
            if (this.leaveTypeId && this.leave_types.length > 0) {
                const leave = this.leave_types.find(x => x.leaveTypeId == this.leaveTypeId)
                return leave ? leave.leaveName : ''
            }
            return ''
        },
        leave_types_filtered() {
            return this.leave_types.filter(x => x.assignThruAllocation || x.leaveCode == 'OFFSET')
        },
    }
}
</script>

<style scoped>
.section-layout {
    padding: 0px 23px 0px 23px;
}
</style>
